import { Episode, appendObservation, createEpisode, makeObservation } from './clinical/episode'

export type DemoScenarioId = 'dka' | 'dka_no_bohb' | 'no_ketones' | 'ketones_later' | 'hhs' | 'mixed'

export interface DemoScenario {
  id: DemoScenarioId
  title: string
  description: string
  build: (endAt?: Date) => Episode
}

const at = (endAt: Date, hoursBefore: number) =>
  new Date(endAt.getTime() - hoursBefore * 3_600_000).toISOString()

function buildDka(endAt = new Date()): Episode {
  const t0 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 480, sodiumMmolL: 136,
    potassiumMmolL: 4.6, chlorideMmolL: 100, bicarbonateMmolL: 12, ph: 7.18,
    betaHydroxybutyrateMmolL: 5.6, ureaMmolL: 10, mentalStatus: 'alert'
  }, at(endAt, 6), { insulinUnitsHour: 7, fluidMlHour: 500 })

  const t1 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 390, sodiumMmolL: 137,
    potassiumMmolL: 4.0, bicarbonateMmolL: 13, ph: 7.21,
    betaHydroxybutyrateMmolL: 4.2, mentalStatus: 'alert'
  }, at(endAt, 5), { insulinUnitsHour: 7, fluidMlHour: 350 })

  const t2 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 240, sodiumMmolL: 138,
    potassiumMmolL: 3.6, chlorideMmolL: 108, bicarbonateMmolL: 16, ph: 7.26,
    betaHydroxybutyrateMmolL: 2.2, mentalStatus: 'alert'
  }, at(endAt, 2), { insulinUnitsHour: 3.5, fluidMlHour: 250 })

  const t3 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 180, sodiumMmolL: 140,
    potassiumMmolL: 4.0, chlorideMmolL: 109, bicarbonateMmolL: 19, ph: 7.32,
    betaHydroxybutyrateMmolL: 0.4, mentalStatus: 'alert'
  }, endAt.toISOString(), { insulinUnitsHour: 3.5, fluidMlHour: 200 })

  return appendObservation(appendObservation(appendObservation(createEpisode(t0), t1), t2), t3)
}

function buildDkaNoBohb(endAt = new Date()): Episode {
  const t0 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 470, sodiumMmolL: 136,
    potassiumMmolL: 4.5, chlorideMmolL: 100, bicarbonateMmolL: 12, ph: 7.18,
    urineKetones: 3, ureaMmolL: 10, mentalStatus: 'alert'
  }, at(endAt, 6), { insulinUnitsHour: 7, fluidMlHour: 500 })

  const t1 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 300, sodiumMmolL: 138,
    potassiumMmolL: 3.9, bicarbonateMmolL: 15, ph: 7.25,
    urineKetones: 2, mentalStatus: 'alert'
  }, at(endAt, 3), { insulinUnitsHour: 7, fluidMlHour: 300 })

  const t2 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 180, sodiumMmolL: 140,
    potassiumMmolL: 4.0, chlorideMmolL: 109, bicarbonateMmolL: 19, ph: 7.32,
    urineKetones: 1, mentalStatus: 'alert'
  }, endAt.toISOString(), { insulinUnitsHour: 3.5, fluidMlHour: 200 })

  return appendObservation(appendObservation(createEpisode(t0), t1), t2)
}

function buildNoKetones(endAt = new Date()): Episode {
  const t0 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 690, sodiumMmolL: 145,
    potassiumMmolL: 4.5, chlorideMmolL: 103, bicarbonateMmolL: 14, ph: 7.20,
    ureaMmolL: 12, mentalStatus: 'drowsy'
  }, at(endAt, 1), { fluidMlHour: 500 })

  const t1 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 580, sodiumMmolL: 145,
    potassiumMmolL: 4.1, chlorideMmolL: 108, bicarbonateMmolL: 16, ph: 7.25,
    ureaMmolL: 11, mentalStatus: 'alert'
  }, endAt.toISOString(), { fluidMlHour: 350 })

  return appendObservation(createEpisode(t0), t1)
}

function buildKetonesLater(endAt = new Date()): Episode {
  const t0 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 700, sodiumMmolL: 145,
    potassiumMmolL: 4.4, chlorideMmolL: 103, bicarbonateMmolL: 14, ph: 7.20,
    ureaMmolL: 12, mentalStatus: 'drowsy'
  }, at(endAt, 1), { fluidMlHour: 600 })

  const t1 = makeObservation({
    knownDiabetes: true, weightKg: 70, glucoseMgDl: 650, sodiumMmolL: 145,
    potassiumMmolL: 4.0, chlorideMmolL: 106, bicarbonateMmolL: 15, ph: 7.23,
    betaHydroxybutyrateMmolL: 5.0, ureaMmolL: 11, mentalStatus: 'alert'
  }, endAt.toISOString(), { fluidMlHour: 500 })

  return appendObservation(createEpisode(t0), t1)
}

function buildHhs(endAt = new Date()): Episode {
  const t0 = makeObservation({
    knownDiabetes: true, weightKg: 75, glucoseMgDl: 780, sodiumMmolL: 147,
    potassiumMmolL: 4.5, bicarbonateMmolL: 22, ph: 7.36,
    betaHydroxybutyrateMmolL: 1.0, ureaMmolL: 14, mentalStatus: 'drowsy'
  }, at(endAt, 1), { fluidMlHour: 750 })

  const t1 = makeObservation({
    knownDiabetes: true, weightKg: 75, glucoseMgDl: 610, sodiumMmolL: 143,
    potassiumMmolL: 4.1, bicarbonateMmolL: 22, ph: 7.36,
    betaHydroxybutyrateMmolL: 1.0, ureaMmolL: 12, mentalStatus: 'drowsy'
  }, endAt.toISOString(), { insulinUnitsHour: 3.8, fluidMlHour: 500 })

  return appendObservation(createEpisode(t0), t1)
}

function buildMixed(endAt = new Date()): Episode {
  const t0 = makeObservation({
    knownDiabetes: true, weightKg: 68, glucoseMgDl: 690, sodiumMmolL: 144,
    potassiumMmolL: 4.8, chlorideMmolL: 103, bicarbonateMmolL: 13, ph: 7.19,
    betaHydroxybutyrateMmolL: 5.1, ureaMmolL: 12, mentalStatus: 'drowsy'
  }, at(endAt, 1), { insulinUnitsHour: 6.8, fluidMlHour: 500 })

  const t1 = makeObservation({
    knownDiabetes: true, weightKg: 68, glucoseMgDl: 580, sodiumMmolL: 145,
    potassiumMmolL: 4.0, chlorideMmolL: 107, bicarbonateMmolL: 15, ph: 7.24,
    betaHydroxybutyrateMmolL: 3.8, ureaMmolL: 11, mentalStatus: 'alert'
  }, endAt.toISOString(), { insulinUnitsHour: 6.8, fluidMlHour: 350 })

  return appendObservation(createEpisode(t0), t1)
}

export const demoScenarios: DemoScenario[] = [
  { id: 'dka', title: 'DKA classica', description: 'Quattro rilevazioni fino ai criteri biochimici di risoluzione con β-OHB disponibile.', build: buildDka },
  { id: 'dka_no_bohb', title: 'DKA · β-OHB non disponibile', description: 'Chetonuria diagnostica e recupero acido-base senza falsa risoluzione automatica.', build: buildDkaNoBohb },
  { id: 'no_ketones', title: 'Chetoni non disponibili', description: 'Pattern iperosmolare con acidosi: crisi non confermata, guardrail attivi e nessuna dose insulinica automatica.', build: buildNoKetones },
  { id: 'ketones_later', title: 'Chetoni disponibili dopo 1 h', description: 'Parte non classificato; β-OHB successivo conferma una forma mista DKA/HHS mantenendo la timeline precedente.', build: buildKetonesLater },
  { id: 'hhs', title: 'HHS · correzione rapida', description: 'Due rilevazioni con alert su velocità di riduzione glicemica e osmolalità.', build: buildHhs },
  { id: 'mixed', title: 'Forma mista DKA/HHS', description: 'Diagnosi d’ingresso mista; lo snapshot successivo migliora senza perdere il fenotipo dell’episodio.', build: buildMixed }
]
